import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { ShieldCheck, Search, AlertCircle } from "lucide-react";
import { bsLabel } from "@/lib/bsDate";

export default function Tracker() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("complaint_number") || "";
  const [cnum, setCnum] = useState(initial);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (val) => {
    const q = (val ?? cnum).trim();
    if (!q) return;
    setLoading(true); setError(""); setData(null);
    try {
      const r = await fetch(`${API}/public/track/${encodeURIComponent(q.toUpperCase())}`);
      if (r.status === 404) { setError("No complaint found with that number. Please check and try again."); return; }
      if (!r.ok) { setError("Service temporarily unavailable. Try again shortly."); return; }
      setData(await r.json());
      setParams({ complaint_number: q.toUpperCase() }, { replace: true });
    } catch {
      setError("Could not reach the server.");
    } finally { setLoading(false); }
  };

  React.useEffect(() => { if (initial) search(initial); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tracker-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center">
            <ShieldCheck size={18} />
          </div>
          <div>
            <div className="font-heading font-bold text-slate-900">Ventures International</div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">Customer Service Tracker</div>
          </div>
          <a href="/login" className="ml-auto text-xs text-slate-500 hover:text-slate-900" data-testid="staff-login-link">Staff login →</a>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Track your complaint</h1>
        <p className="text-sm text-slate-500 mt-1">Enter the complaint number you received (e.g., VI-260523-0001) to view live status.</p>
        <div className="mt-6 flex gap-2">
          <Input
            value={cnum}
            onChange={(e) => setCnum(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="VI-YYMMDD-NNNN"
            className="font-mono"
            data-testid="track-input"
          />
          <Button onClick={() => search()} disabled={loading} className="bg-slate-900 text-white" data-testid="track-btn">
            <Search size={14} className="mr-1" /> {loading ? "Searching..." : "Track"}
          </Button>
        </div>

        {error && (
          <Card className="mt-5"><CardContent className="p-4 flex items-start gap-2">
            <AlertCircle size={16} className="text-rose-600 mt-0.5" />
            <div className="text-sm text-rose-700" data-testid="track-error">{error}</div>
          </CardContent></Card>
        )}

        {data && (
          <Card className="mt-6" data-testid="track-result">
            <CardContent className="p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Complaint No.</div>
                  <div className="font-heading font-bold text-2xl font-mono text-slate-900">{data.complaint_number}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Current Status</div>
                  <div className="mt-1"><StatusBadge status={data.status} /></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mt-6">
                <Info label="Customer" value={data.customer_name} />
                <Info label="Brand / Product" value={`${data.brand || ""} ${data.product_name || ""}`} />
                <Info label="Registered" value={`${data.registered_date || "—"}${data.registered_date ? ` (BS ${bsLabel(data.registered_date)})` : ""}`} />
                <Info label="Ageing" value={data.ageing_days != null ? `${data.ageing_days} days` : "—"} />
                <Info label="Nature" value={data.nature_of_complaint} />
                <Info label="Warranty" value={data.warranty_status} />
                <Info label="Service Type" value={data.service_type} />
                <Info label="Technician" value={data.technician_name || "Not yet assigned"} />
                <Info label="First Visit" value={data.technician_first_visit_date || "—"} />
                <Info label="Closing Date" value={data.closing_date || "—"} />
              </div>

              <div className="mt-7">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Status Timeline</div>
                <div className="space-y-0.5">
                  {data.timeline.length === 0 && <div className="text-sm text-slate-400">No history yet.</div>}
                  {data.timeline.map((t, i) => (
                    <div key={i} className="timeline-item">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{t.action}</div>
                          {t.status && <div className="text-xs text-slate-600">{t.status}</div>}
                        </div>
                        <div className="text-xs text-slate-400">{new Date(t.timestamp).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-xs text-slate-400 text-center mt-10">
          Need help? Call Ventures International Service Centre. Hours: Sun–Fri, 9am – 6pm NPT.
        </div>
      </main>
    </div>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}
